import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  ContractWithSupplier,
  ContractFormData,
  ContractUpdateData,
  ContractStatus,
  ContractStatusHistory,
  ContractFilters,
  Contract,
} from '../types'

const QUERY_KEY = 'contracts'
const HISTORY_QUERY_KEY = 'contract_status_history'

/**
 * Hook pour récupérer les contrats d'une résidence
 */
export function useContracts(filters: ContractFilters) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: async (): Promise<ContractWithSupplier[]> => {
      let query = supabase
        .from('contracts')
        .select(`
          *,
          suppliers(id, code, name, email, phone)
        `)
        .eq('residence_id', filters.residence_id)
        .order('start_date', { ascending: false })

      if (filters.supplier_id) {
        query = query.eq('supplier_id', filters.supplier_id)
      }

      if (filters.status) {
        query = query.eq('status', filters.status)
      }

      if (filters.contract_type) {
        query = query.eq('contract_type', filters.contract_type)
      }

      if (filters.search) {
        query = query.or(
          `reference.ilike.%${filters.search}%,label.ilike.%${filters.search}%`
        )
      }

      const { data, error } = await query
      if (error) throw error
      return data as ContractWithSupplier[]
    },
    enabled: !!filters.residence_id,
  })
}

/**
 * Hook pour récupérer le détail d'un contrat
 */
export function useContract(contractId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', contractId],
    queryFn: async (): Promise<ContractWithSupplier> => {
      const { data, error } = await supabase
        .from('contracts')
        .select(`
          *,
          suppliers(*)
        `)
        .eq('id', contractId!)
        .single()

      if (error) throw error
      return data as ContractWithSupplier
    },
    enabled: !!contractId,
  })
}

/**
 * Génère une référence de contrat unique (CTR-YYYYMMDD-XXXX)
 */
function generateContractReference(): string {
  const date = new Date()
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '')
  const random = Math.random().toString(36).substring(2, 6).toUpperCase()
  return `CTR-${dateStr}-${random}`
}

/**
 * Hook pour créer un contrat
 */
export function useCreateContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (formData: ContractFormData) => {
      const { data, error } = await supabase
        .from('contracts')
        .insert({ ...formData, reference: generateContractReference() })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Hook pour mettre à jour un contrat
 */
export function useUpdateContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ContractUpdateData }) => {
      const { data: result, error } = await supabase
        .from('contracts')
        .update(data)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Hook pour changer le statut d'un contrat
 */
export function useUpdateContractStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string
      status: ContractStatus
      reason?: string
    }) => {
      // Le trigger log_contract_status_change enregistre automatiquement l'historique
      const { data, error } = await supabase
        .from('contracts')
        .update({ status })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY, variables.id] })
    },
  })
}

/**
 * Hook pour supprimer un contrat
 */
export function useDeleteContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('contracts').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Hook pour récupérer l'historique des statuts d'un contrat
 */
export function useContractStatusHistory(contractId?: string) {
  return useQuery({
    queryKey: [HISTORY_QUERY_KEY, contractId],
    queryFn: async (): Promise<ContractStatusHistory[]> => {
      const { data, error } = await supabase
        .from('contract_status_history')
        .select('*')
        .eq('contract_id', contractId!)
        .order('changed_at', { ascending: false })

      if (error) throw error
      return data as ContractStatusHistory[]
    },
    enabled: !!contractId,
  })
}

/**
 * Hook pour récupérer les contrats actifs d'un fournisseur dans une résidence
 */
export function useSupplierActiveContracts(residenceId?: string, supplierId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'active', residenceId, supplierId],
    queryFn: async (): Promise<Pick<Contract, 'id' | 'reference' | 'label' | 'contract_type' | 'billing_frequency' | 'default_account_id' | 'default_budget_id'>[]> => {
      const { data, error } = await supabase
        .from('contracts')
        .select('id, reference, label, contract_type, billing_frequency, default_account_id, default_budget_id')
        .eq('residence_id', residenceId!)
        .eq('supplier_id', supplierId!)
        .eq('status', 'ACTIVE')
        .order('label')

      if (error) throw error
      return data as Pick<Contract, 'id' | 'reference' | 'label' | 'contract_type' | 'billing_frequency' | 'default_account_id' | 'default_budget_id'>[]
    },
    enabled: !!residenceId && !!supplierId,
  })
}

/**
 * Hook pour récupérer les contrats arrivant à échéance
 */
export function useExpiringContracts(residenceId?: string, daysAhead: number = 30) {
  return useQuery({
    queryKey: [QUERY_KEY, 'expiring', residenceId, daysAhead],
    queryFn: async (): Promise<ContractWithSupplier[]> => {
      const futureDate = new Date()
      futureDate.setDate(futureDate.getDate() + daysAhead)

      const { data, error } = await supabase
        .from('contracts')
        .select(`
          *,
          suppliers(id, code, name, email, phone)
        `)
        .eq('residence_id', residenceId!)
        .eq('status', 'ACTIVE')
        .not('end_date', 'is', null)
        .lte('end_date', futureDate.toISOString().split('T')[0])
        .order('end_date')

      if (error) throw error
      return data as ContractWithSupplier[]
    },
    enabled: !!residenceId,
  })
}
